/**
 * Dependencies.
 */
import { Component, Mixins } from 'vue-property-decorator';

import ServicePointModel from '@/api/address/service-point.model';
import {
  ORDER_REQUEST_STATUS,
  ORDER_REQUEST_TYPES
} from '@/modules/tracking/constants';
import TimelineMixin from '@/modules/tracking/mixins/timeline.mixin';
import TrackingMixin from '@/modules/tracking/mixins/tracking.mixin';
import { trackingStore } from '@/modules/tracking/store';
import configuration from '@/plugins/configuration.plugin';
import Error from '@/ui/components/error.component.vue';
import NotifyClientRequest from '@/ui/components/notify/client-request.component.vue';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: {
    Error,
    NotifyClientRequest,
    Timeline
  },
  name: 'pick-up-confirm-scene'
})
export default class extends Mixins(TrackingMixin, TimelineMixin) {
  /**
   * Property `isSaving`.
   */
  isSaving = false;

  /**
   * Property `isSnackbarVisible`.
   */
  isSnackbarVisible = false;

  /**
   * Property `isScheduleExpanded`.
   */
  isScheduleExpanded = true;

  /**
   * Property `distributionCenterRadius`.
   */
  distributionCenterRadius = null;

  /**
   * Property `servicePoint`.
   */
  servicePoint: ServicePointModel = null;

  /**
   * Getter `newDate`.
   */
  get newDate() {
    const { dayChanged: date = null } = trackingStore || {};

    return this.$moment(date).isValid()
      ? this.$moment(date).format('DD MMM YYYY')
      : null;
  }

  /**
   * Getter has today address request.
   */
  get hasTodayAddressRequest() {
    const { requests = [] } = trackingStore || {};
    const today = this.$moment().startOf('day');

    const todayDateRequest = requests.filter(
      ({ recordDate = null, status, type }) =>
        this.$moment(recordDate).isValid() &&
        this.$moment(recordDate)
          .startOf('day')
          .isSame(today) &&
        type === ORDER_REQUEST_TYPES.CHANGE_ADDRESS &&
        (status.code === ORDER_REQUEST_STATUS.AUTOMATIC_PENDING_TREATMENT ||
          status.code === ORDER_REQUEST_STATUS.TREATED)
    );

    return todayDateRequest.length > 0;
  }

  /**
   * Getter `canShowSchedule`.
   */
  get canShowSchedule() {
    const { schedule } = this || {};

    return schedule.length;
  }

  /**
   * Getter `schedule`.
   */
  get schedule() {
    const schedule = [];

    const { workDays = null } = this.servicePoint || {};

    if (workDays) {
      Object.keys(workDays).forEach(day => {
        const {
          afternoonEndTime = null,
          afternoonStartTime = null,
          isActive = false,
          morningEndTime = null,
          morningStartTime = null
        } = workDays[day] || {};

        if (isActive) {
          schedule.push({
            afternoonEndTime: afternoonEndTime
              ? afternoonEndTime.substring(0, 5)
              : null,
            afternoonStartTime: afternoonStartTime
              ? afternoonStartTime.substring(0, 5)
              : null,
            day: Number(day),
            isActive,
            morningEndTime: morningEndTime
              ? morningEndTime.substring(0, 5)
              : null,
            morningStartTime: morningStartTime
              ? morningStartTime.substring(0, 5)
              : null
          });
        }
      });
    }

    return schedule;
  }

  /**
   * Lifecycle mounted hook.
   */
  async mounted() {
    const { servicePointChanged } = trackingStore || {};

    this.servicePoint = servicePointChanged;

    if (!this.servicePoint) {
      this.handleForceTrackingScene();
    } else {
      this.$on('trackingDataUpdated', async () => {
        const isValid = this.handleIsValidToBeOnScene();

        if (!isValid) {
          this.handleForceTrackingScene();
        }
      });

      await this.handleInitializeMixin();

      const isValid = this.handleIsValidToBeOnScene();

      if (!isValid) {
        this.handleForceTrackingScene();
      } else {
        await this.handleInitialize();
      }
    }
  }

  /**
   * Handle is valid to be on scene.
   */
  handleIsValidToBeOnScene() {
    return this.canCreateChangeRequest && this.canChangeAddressToServicePoint;
  }

  /**
   * Handle initialize.
   */
  async handleInitialize() {
    await this.handleGetConfiguration();
  }

  /**
   * Handle get configuration.
   */
  async handleGetConfiguration() {
    const config = await configuration.getDistributionCenterRadius();
    const { distributionCenterRadius = null } = config || {};

    this.distributionCenterRadius = distributionCenterRadius;
  }

  /**
   * Handle day schedule.
   */
  handleDaySchedule({ day }: { day: number }) {
    const { schedule = [] } = this;

    const dayItem = schedule.find(({ day: itemDay }) => itemDay === day);

    const {
      afternoonEndTime = null,
      afternoonStartTime = null,
      morningEndTime = null,
      morningStartTime = null
    } = dayItem || {};

    const daySchedule =
      morningStartTime &&
      morningEndTime &&
      afternoonStartTime &&
      afternoonEndTime
        ? `${morningStartTime}-${morningEndTime} ${afternoonStartTime}-${afternoonEndTime}`
        : `${morningStartTime || ''}${
          morningStartTime && afternoonEndTime ? `-${afternoonEndTime}` : '-'
        }`;

    return daySchedule;
  }

  /**
   * Handle confirm.
   */
  async handleConfirm() {
    const { hasTodayAddressRequest, servicePoint } = this;
    const {
      code: servicePointCode,
      hasDockingDay,
      hasRadiusDistance,
      hasWorkingDay
    } = servicePoint || {};
    const { dayChanged: date = null } = trackingStore;
    const isForAutomaticApproval =
      (!hasDockingDay &&
        hasRadiusDistance &&
        hasWorkingDay &&
        !hasTodayAddressRequest) ||
      !!date;

    try {
      this.isSaving = true;

      const {
        city,
        countryCode,
        countryName,
        doorNumber,
        latitude,
        longitude,
        street,
        zipCode
      } = this.servicePoint || {};

      const address = {
        city,
        countryCode,
        countryName,
        doorNumber,
        isForAutomaticApproval,
        latitude,
        longitude,
        servicePointCode,
        street,
        zipCode
      };

      trackingStore.setChangeDeliveryAddress({ address });

      this.$router.push({
        name: 'ConfirmChange',
        params: {
          key: this.key,
          type: date ? 'day_address' : 'address'
        }
      });
    } catch (error) {
      this.isSnackbarVisible = true;

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleConfirm',
          scene: 'PickUpConfirmScene'
        });
      }
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Handle cancel.
   */
  handleCancel() {
    const { key } = this;

    this.$router.push({
      name: 'Tracking',
      params: { key }
    });
  }
}
